import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Scheduler } from "@aldabil/react-scheduler";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import ViewMode, { ZoomWidget } from "./ViewMode";
import schedulerMuiTheme from "./schedulerMuiTheme";
import { useThemeMode } from "../../contexts/theme-mode";
import { createAppTheme } from "../../theme/mui-theme";

dayjs.extend(utc);
dayjs.extend(timezone);

import CustomEditor from "./CustomEditor";
import ConfirmationModal, { ConfirmationType } from "./ConfirmationModal";
import CheckoutSummaryModal from "./CheckoutSummaryModal";
import {
  weekConfig,
  dayConfig,
  resourceFields,
  groupedSchedules,
  SchedulerAPI,
  SCHEDULER_TIMEZONE,
  formatScheduleUpdateStart,
  getAppointmentServicesForUpdate,
  getAppointmentTimeRange,
  validateSchedulerBookingHours,
  SCHEDULER_HOURS_ERROR,
} from "./config";
import { renderCell } from "./CellItem";
import { getFields } from "./Fields";
import stringToColor from "string-to-color";
import axios from "axios";
import { baseAxios, Context } from "../../utils/config";
import {
  getAssignedLocations,
  worksAtLocation,
} from "../../utils/locationHelpers";
import "./style.scss";
import { useAuth } from "../../contexts/auth";
import notify from "devextreme/ui/notify";
import { toast } from "react-hot-toast";
import CancelModal from "./CancelModal";
import { useScheduler } from "./useScheduler";
import { ScheduleEntryStatus } from "./DetailedItem";
import {
  isCellUnavailable,
  getStylistDayAvailability,
  STYLIST_AVAILABILITY_ERROR,
  validateStylistAvailabilityForAppointment,
  isBlockingTimeRange,
  visibleSchedulerEvents,
  findTimeOffOverlap,
  timeOffToEvents,
  timeOffMinutesWithin,
  STYLIST_TIME_OFF_ERROR,
} from "./availabilityUtils";
import TimeOffDialog from "../../components/time-off/TimeOffDialog";
import {
  formatTimeOffRange,
  TimeOffDeleteDialog,
} from "../../components/time-off/TimeOffList";
import {
  deleteTimeOff,
  isStylistTimeOffError,
} from "../../components/time-off/timeOffApi";
import AppointmentView from "./AppointmentView";
import WeekNavigation from "./WeekNavigation";
import SchedulerCustomHeader from "./SchedulerCustomHeader";
import TimeLine from "./TimeLine";
import TransactionFormModal from "./TransactionFormModal";
import TransactionSummaryModal from "./TransactionSummaryModal";
import RearrangeColumnsModal from "./RearrangeColumnsModal";
import PrintStylistScheduleDialog from "./PrintStylistScheduleDialog";
import {
  mergeStylistOrder,
  parseStylistOrder,
  sortByStylistOrder,
} from "./stylistColumnOrder";
// Register dialogs are global (see `RegisterDialogManager` in Content).
import {
  ROLE_FRONT_DESK,
  ROLE_SALON_ADMIN,
  ROLE_SYSTEM_ADMIN,
  ROLE_JDTECH_ADMIN,
} from "../../utils/constants";
import { Person } from "@mui/icons-material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";

const formatDateTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

/** Popover for a clicked time-off block (JD-465); admins get Edit / Delete. */
function TimeOffViewer({ event, stylists, canManage, onEdit, onDelete }) {
  const block = event.timeOff || {};
  const stylistName = (stylists || []).find(
    (s) => String(s.user_id) === String(block.user_id)
  )?.user_name;
  return (
    <Box sx={{ p: 2, minWidth: 260, maxWidth: 360 }}>
      <Typography variant="subtitle1" fontWeight={700}>
        Time off{stylistName ? ` · ${stylistName}` : ""}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {formatTimeOffRange(block)}
      </Typography>
      {block.note ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          {block.note}
        </Typography>
      ) : null}
      {block.created_by_name ? (
        <Typography variant="caption" color="text.secondary" component="p">
          Recorded by {block.created_by_name}
        </Typography>
      ) : null}
      {canManage ? (
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} justifyContent="flex-end">
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={onDelete}
            sx={{ textTransform: "none" }}
          >
            Delete
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onEdit}
            sx={{ textTransform: "none", boxShadow: "none" }}
          >
            Edit
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
}

function CustomScheduler() {
  const { mode } = useThemeMode();
  const editorTheme = useMemo(() => createAppTheme(mode), [mode]);
  const SCHEDULER_START_HOUR = 9;
  const SCHEDULER_END_HOUR = 20;
  const SLOT_HEIGHT_PX = 30;
  const SLOT_MINUTES = 15;
  /** Vertical offset from zoom container top to first time row (below custom header + resource chrome). */
  const GRID_BODY_OFFSET_WITH_RESOURCES_PX = 92;
  const GRID_BODY_OFFSET_EMPTY_RESOURCES_PX = 45;

  const calendarRef = useRef();
  const schedulerBoxRef = useRef();
  const timelineLeftRef = useRef();
  const timelineRightRef = useRef();
  const schedulerWrapperRef = useRef(null);
  const schedulerCustomHeaderRef = useRef(null);
  // aldabil keeps the first `customViewer` function; read latest handlers from here.
  const viewerApiRef = useRef({});
  const [schedulerHeaderHeightPx, setSchedulerHeaderHeightPx] = useState(0);
  const {
    setEvents,
    setResources,
    view,
    setView,
    events,
    resources,
    selectedDate,
    setSelectedDate,
  } = useScheduler();
  const urlLocation = useLocation();

  const { dataset, setDataset, location } = useContext(Context);
  const [loading, setLoading] = useState(false);
  const [loadMessage, setLoadMessage] = useState("");
  const [loadProgress, setLoadProgress] = useState(null); // { loaded, total } in bytes when downloading
  const [syncing, setSyncing] = useState(false); // Background sync indicator
  const [syncMessage, setSyncMessage] = useState(""); // Sync status message
  const [loadError, setLoadError] = useState(null);
  const [loadEmpty, setLoadEmpty] = useState(false);
  /** When true, show the scheduler grid even if loadEmpty so the user can open "create appointment". */
  const [suppressEmptyCard, setSuppressEmptyCard] = useState(false);
  const pendingCreateFromEmptyRef = useRef(false);
  const xhrRef = useRef(null); // Track current XHR to abort on unmount/refetch
  const fetchIdRef = useRef(0); // Track fetch ID to ignore stale responses
  const [cancellingAppointmentId, setCancellingAppointmentId] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  const [appointmentIdToCopy, setAppointmentIdToCopy] = useState(null);
  const [currentQuarterTop, setCurrentQuarterTop] = useState(null);
  const processingCopyRef = useRef(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(urlLocation.search);
    const dateParam = queryParams.get("date");
    const viewParam = queryParams.get("view");

    if (viewParam === "day" || viewParam === "week" || viewParam === "month") {
      setView(viewParam);
    }

    if (dateParam) {
      const nextSelected = dayjs(dateParam)
        .tz(SCHEDULER_TIMEZONE)
        .startOf("day")
        .toDate();
      setSelectedDate(nextSelected);
      calendarRef.current?.scheduler?.handleState(nextSelected, "selectedDate");
    }
  }, [urlLocation.search, setSelectedDate, setView]);

  useEffect(() => {
    const updateCurrentQuarterBlock = () => {
      const now = dayjs().tz(SCHEDULER_TIMEZONE);
      const isSelectedDateToday =
        selectedDate != null &&
        dayjs(selectedDate).tz(SCHEDULER_TIMEZONE).isSame(now, "day");

      // Show the "current time" indicator only for today's Day view.
      if (view !== "day" || !isSelectedDateToday) {
        setCurrentQuarterTop(null);
        return;
      }

      const gridTopOffsetPx =
        schedulerHeaderHeightPx +
        ((resources?.length ?? 0) === 0
          ? GRID_BODY_OFFSET_EMPTY_RESOURCES_PX
          : GRID_BODY_OFFSET_WITH_RESOURCES_PX);

      const startOfSchedule = now
        .startOf("day")
        .hour(SCHEDULER_START_HOUR)
        .minute(0)
        .second(0)
        .millisecond(0);
      const endOfSchedule = now
        .startOf("day")
        .hour(SCHEDULER_END_HOUR)
        .minute(0)
        .second(0)
        .millisecond(0);

      if (now.isBefore(startOfSchedule) || !now.isBefore(endOfSchedule)) {
        setCurrentQuarterTop(null);
        return;
      }

      const minutesFromStart = now.diff(startOfSchedule, "minute");
      const quarterIndex = Math.floor(minutesFromStart / SLOT_MINUTES);
      setCurrentQuarterTop(gridTopOffsetPx + quarterIndex * SLOT_HEIGHT_PX);
    };

    updateCurrentQuarterBlock();
    const intervalId = window.setInterval(updateCurrentQuarterBlock, 60000);
    return () => window.clearInterval(intervalId);
  }, [view, selectedDate, resources?.length, schedulerHeaderHeightPx]);

  useLayoutEffect(() => {
    const headerEl = schedulerCustomHeaderRef.current;
    const wrapperEl = schedulerWrapperRef.current;
    if (!headerEl || !wrapperEl) return;

    const apply = () => {
      const h = Math.ceil(headerEl.getBoundingClientRect().height);
      setSchedulerHeaderHeightPx(h);
      wrapperEl.style.setProperty("--scheduler-custom-header-height", `${h}px`);
    };

    apply();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", apply);
      return () => window.removeEventListener("resize", apply);
    }

    const ro = new ResizeObserver(() => apply());
    ro.observe(headerEl);
    return () => ro.disconnect();
  }, [
    view,
    loading,
    loadEmpty,
    suppressEmptyCard,
    events?.length,
    dataset?.stylists?.length,
  ]);

  const getStylistAvailabilityStats = (stylist, allEvents, date, timeOff) => {
    const dayAvailability = getStylistDayAvailability(stylist, date);

    if (!dayAvailability) {
      return { bookedPct: 0, availablePct: 0 };
    }

    const [startHour, startMinute] = dayAvailability.start_time
      .split(":")
      .map(Number);
    const [endHour, endMinute] = dayAvailability.end_time
      .split(":")
      .map(Number);

    const availabilityStart = dayjs(date)
      .hour(startHour)
      .minute(startMinute)
      .second(0);
    const availabilityEnd = dayjs(date)
      .hour(endHour)
      .minute(endMinute)
      .second(0);

    // Time off inside the day's hours is unavailable time (JD-465).
    const totalMinutes =
      availabilityEnd.diff(availabilityStart, "minute") -
      timeOffMinutesWithin(
        timeOff,
        stylist.user_id,
        availabilityStart,
        availabilityEnd
      );

    if (!totalMinutes || totalMinutes <= 0) {
      return { bookedPct: 0, availablePct: 0 };
    }

    const stylistEvents = (allEvents || []).filter(
      (e) =>
        e.user_id === stylist.user_id &&
        e.appointment_status !== ScheduleEntryStatus.Canceled &&
        dayjs(e.start).isSame(date, "day")
    );

    let bookedMinutes = 0;

    stylistEvents.forEach((event) => {
      (event.services || []).forEach((service) => {
        const serviceStart = dayjs(service.serviceStart);
        const serviceEnd = dayjs(service.serviceEnd);

        const effectiveStart = serviceStart.isBefore(availabilityStart)
          ? availabilityStart
          : serviceStart;
        const effectiveEnd = serviceEnd.isAfter(availabilityEnd)
          ? availabilityEnd
          : serviceEnd;

        if (effectiveEnd.isAfter(effectiveStart)) {
          // Minutes inside time off are already out of totalMinutes, so they
          // are not booked capacity either (JD-465).
          bookedMinutes +=
            effectiveEnd.diff(effectiveStart, "minute") -
            timeOffMinutesWithin(
              timeOff,
              stylist.user_id,
              effectiveStart,
              effectiveEnd
            );
        }
      });
    });

    const bookedPct = (bookedMinutes / totalMinutes) * 100;
    const availablePct = Math.max(0, 100 - bookedPct);

    return {
      bookedPct: Number.isFinite(bookedPct) ? bookedPct : 0,
      availablePct: Number.isFinite(availablePct) ? availablePct : 0,
    };
  };

  const ResourceHeader = ({ resource }) => {
    const bookedRaw = Number(resource?.bookedPct ?? 0);
    const availableRaw = Number(resource?.availablePct ?? 0);
    const bookedPct = Math.max(0, Math.min(100, bookedRaw));
    const availablePct = Math.max(0, Math.min(100, availableRaw));
    const showUtilization = view === "day";

    const bookedRounded = Math.round(bookedPct);
    const availableRounded = Math.round(availablePct);
    const bookedText = `${bookedRounded}%`;
    const availableText = `${availableRounded}%`;

    return (
      <div className="scheduler-resource-header">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            paddingLeft: "4px",
          }}
        >
          <div
            className="scheduler-resource-header__avatar"
            style={{
              backgroundColor: resource?.color || "#e9ecef",
              borderBottom: `1px solid ${resource?.color || "#e9ecef"}`,
            }}
          >
            <Person sx={{ fontSize: "18px", opacity: 0.8 }} />
          </div>

          <div className="scheduler-resource-header__name">
            {resource?.user_name || ""}
          </div>
        </div>

        {showUtilization && (
          <>
            <div
              className="scheduler-resource-header__bar"
              role="img"
              aria-label={`Booked ${bookedText}, Available ${availableText}`}
            >
              <div
                className="scheduler-resource-header__bar-booked"
                style={{ width: `${bookedPct}%` }}
              />
              <div
                className="scheduler-resource-header__bar-available"
                style={{ width: `${availablePct}%` }}
              />
            </div>

            <div className="scheduler-resource-header__labels">
              {bookedRounded > 0 ? (
                <span className="scheduler-resource-header__label-booked">
                  {bookedText} booked
                </span>
              ) : (
                <span className="scheduler-resource-header__label-spacer" />
              )}
              <span className="scheduler-resource-header__label-available">
                Avail {availableText}
              </span>
            </div>
          </>
        )}
      </div>
    );
  };

  const [dialogPayload, setDialogPayload] = useState(null);
  // Stylist time off (JD-465): grid events, the shared dialog, delete confirm.
  const [timeOffEvents, setTimeOffEvents] = useState([]);
  const [timeOffDialog, setTimeOffDialog] = useState(null); // { timeOff? }
  const [timeOffToDelete, setTimeOffToDelete] = useState(null);
  const [deletingTimeOff, setDeletingTimeOff] = useState(false);
  const [zoom, setZoom] = useState(100);
  const [stylistOrder, setStylistOrder] = useState([]);
  const [rearrangeOpen, setRearrangeOpen] = useState(false);
  const [printScheduleOpen, setPrintScheduleOpen] = useState(false);
  const [savingColumnOrder, setSavingColumnOrder] = useState(false);

  // Walk-in transaction states
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [showTransactionSummary, setShowTransactionSummary] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [transactionFormData, setTransactionFormData] = useState(null); // For preserving form data on back

  const { user } = useAuth();
  let userCredentials = {
    Barcode: user.barcode,
    Role: user.role,
    Organization: user.org,
    Location: location?.location_id !== -1 ? location?.location_id : user.location_id,
  };

  const canSelectAnyLocation = user?.userRoles?.some(
    (r) =>
      r === ROLE_SYSTEM_ADMIN ||
      r === ROLE_JDTECH_ADMIN ||
      r === ROLE_SALON_ADMIN
  );

  // Same roles the API allows to record, edit and delete time off.
  const canManageTimeOff = user?.userRoles?.some(
    (r) =>
      r === ROLE_SYSTEM_ADMIN ||
      r === ROLE_JDTECH_ADMIN ||
      r === ROLE_SALON_ADMIN
  );

  // JD-293: "All Locations" (-1) is no longer added here — the global header
  // selector handles that. This list is used by CustomEditor for appointment
  // location selection, where -1 is not a valid choice.
  const buildLocations = (apiLocations) => {
    if (canSelectAnyLocation) {
      return apiLocations || [];
    }
    return getAssignedLocations(user).map((assigned) => {
      const fromApi = (apiLocations || []).find(
        (loc) => Number(loc.location_id) === Number(assigned.location_id)
      );
      return (
        fromApi || {
          location_id: assigned.location_id,
          location_name: assigned.location_name,
          organization_id: parseInt(user.org),
        }
      );
    });
  };

  // Cash register status (for button label only; dialogs are global)
  const [registerStatus, setRegisterStatus] = useState(null);

  const hasSpecificLocation = Number(location?.location_id) > 0;
  const effectiveLocationId = hasSpecificLocation ? location.location_id : null;

  const fetchRegisterStatus = useCallback(async () => {
    if (!effectiveLocationId) {
      setRegisterStatus(null);
      return;
    }
    try {
      const res = await baseAxios.get("/register/status", {
        params: { locationId: effectiveLocationId },
      });
      setRegisterStatus(res.data);
    } catch (err) {
      console.error("Failed to load register status", err);
      setRegisterStatus(null);
    }
  }, [effectiveLocationId]);

  useEffect(() => {
    fetchRegisterStatus();
  }, [fetchRegisterStatus]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await baseAxios.get(SchedulerAPI.columnOrder);
        if (cancelled) return;
        if (Array.isArray(res.data?.stylistOrder)) {
          setStylistOrder(parseStylistOrder(res.data.stylistOrder));
        }
      } catch (err) {
        console.error("Failed to load scheduler column order", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep button label in sync whenever the global manager opens/closes the register.
  useEffect(() => {
    const handler = () => fetchRegisterStatus();
    window.addEventListener("register-status-updated", handler);
    return () => window.removeEventListener("register-status-updated", handler);
  }, [fetchRegisterStatus]);

  const canManageRegister = user?.userRoles?.some(
    (role) => role === ROLE_FRONT_DESK || role === ROLE_SALON_ADMIN
  );

  // Keep the button clickable when "All Locations" is selected so the global
  // manager can toast "select a specific location".
  const isRegisterOpen = Boolean(registerStatus?.isOpen);
  const registerReady = hasSpecificLocation ? Boolean(registerStatus) : true;

  const handleOpenRegisterClick = () => {
    window.dispatchEvent(new Event("register-open-request"));
  };

  const handleCloseoutClick = () => {
    window.dispatchEvent(new Event("register-closeout-request"));
  };

  const handleOpeningClick = () => {
    window.dispatchEvent(new Event("register-opening-request"));
  };

  const registerButtonSx = {
    height: "40px",
    maxHeight: "40px",
    minHeight: "40px",
    borderRadius: "6px",
    boxShadow: "none",
    lineHeight: "1",
    textTransform: "none",
    fontWeight: 700,
    "&:hover": { boxShadow: "none" },
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fetchRemote = useCallback(() => {
    // Abort any previous XHR request
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }

    // Increment fetch ID to track this specific fetch
    const currentFetchId = ++fetchIdRef.current;

    setLoading(true);
    setLoadError(null);
    setLoadEmpty(false);
    setSuppressEmptyCard(false);
    setLoadMessage("Requesting your schedule to the server…");
    setLoadProgress(null);

    return baseAxios
      .post(SchedulerAPI.get, userCredentials)
      .then(async (res) => {
        // Check if this fetch is still current
        if (currentFetchId !== fetchIdRef.current) return;

        let data = res.data;

        let hadDownloadProgress = false;
        if (data?.dataUrl) {
          hadDownloadProgress = true;
          setLoadMessage("Downloading appointment data…");
          setLoadProgress({ loaded: 0, total: 0 });

          try {
            data = await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhrRef.current = xhr; // Store reference for potential abort
              const state = { done: false, maxLoaded: 0, total: 0 };

              xhr.open("GET", data.dataUrl);
              xhr.responseType = "json";

              xhr.onprogress = (e) => {
                // Ignore if this fetch is no longer current
                if (currentFetchId !== fetchIdRef.current) return;
                if (state.done) return;
                if (e.loaded <= state.maxLoaded) return;
                state.maxLoaded = e.loaded;
                if (e.lengthComputable && e.total > 0) {
                  state.total = e.total;
                }
                setLoadProgress({
                  loaded: e.loaded,
                  total: state.total || null,
                });
              };

              xhr.onload = () => {
                state.done = true;
                xhrRef.current = null;
                // Ignore if this fetch is no longer current
                if (currentFetchId !== fetchIdRef.current) return;

                if (xhr.status >= 200 && xhr.status < 300) {
                  // Try to get total from headers if we don't have it
                  if (state.total === 0) {
                    const contentLength =
                      xhr.getResponseHeader("content-length");
                    if (contentLength) {
                      state.total = parseInt(contentLength, 10);
                    }
                  }
                  // Show 100%
                  const finalSize =
                    state.total > 0 ? state.total : state.maxLoaded || 1;
                  setLoadProgress({ loaded: finalSize, total: finalSize });
                  // Brief delay to show 100%, then transition
                  setTimeout(() => {
                    if (currentFetchId !== fetchIdRef.current) return;
                    setLoadProgress(null);
                    setLoadMessage("Preparing your schedule…");
                    resolve(xhr.response);
                  }, 150);
                } else {
                  setLoadProgress(null);
                  reject(new Error(`Failed to load: ${xhr.status}`));
                }
              };

              xhr.onerror = () => {
                state.done = true;
                xhrRef.current = null;
                if (currentFetchId !== fetchIdRef.current) return;
                setLoadProgress(null);
                reject(new Error("Network error"));
              };

              xhr.onabort = () => {
                state.done = true;
                xhrRef.current = null;
                reject(new Error("Request aborted"));
              };

              xhr.send();
            });
          } catch (err) {
            // Check if this fetch is still current before updating state
            if (currentFetchId !== fetchIdRef.current) return;
            // Ignore abort errors (they're intentional)
            if (err?.message === "Request aborted") return;
            console.error("Error fetching scheduler data from dataUrl:", err);
            setLoading(false);
            setLoadProgress(null);
            setLoadError(
              err?.message ||
                "We couldn't load the schedule data. Please check your connection and try again."
            );
            setLoadMessage("");
            return;
          }
        }

        // Check if this fetch is still current before continuing
        if (currentFetchId !== fetchIdRef.current) return;

        const result = {
          ...data,
          stylists: data?.stylists?.map((stylist) => ({
            ...stylist,
            color: stringToColor(stylist.user_name),
          })),
          schedules: groupedSchedules(
            data?.schedules?.filter(
              (s) =>
                s.location_id === location.location_id ||
                location.location_id === -1
            ),
            SCHEDULER_TIMEZONE
          ),
          locations: buildLocations(data?.locations),
        };

        const noStylists = !result.stylists?.length;
        const noSchedules = !result.schedules?.length;

        // Brief delay for "Preparing your schedule…" to be visible
        await new Promise((r) => setTimeout(r, hadDownloadProgress ? 300 : 0));

        // Final check before updating state
        if (currentFetchId !== fetchIdRef.current) return;

        const loadedOrder = parseStylistOrder(data?.stylistOrder);
        if (Array.isArray(data?.stylistOrder)) {
          setStylistOrder(loadedOrder);
        }

        setDataset(result);
        setEvents(result.schedules);
        setResources(result.stylists || []);
        setLoadProgress(null);
        setLoadEmpty(noStylists && noSchedules);
        setLoadMessage("");
        setLoading(false);
      })
      .catch((err) => {
        // Ignore errors from aborted requests
        if (err?.message === "Request aborted" || err?.message === "canceled")
          return;
        setLoading(false);
        setLoadMessage("");
        setLoadProgress(null);
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "We couldn't load the schedule data. Please check your connection and try again.";
        setLoadError(message);
        notify(message, "error", 3000);
      });
  }, [setDataset, setEvents, setResources, location]);

  // Cleanup on unmount - abort any pending XHR
  useEffect(() => {
    return () => {
      fetchIdRef.current++; // Invalidate any pending fetch
      if (xhrRef.current) {
        xhrRef.current.abort();
        xhrRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const updateTimelinePositions = () => {
      const schedulerBox = schedulerBoxRef.current;
      if (!schedulerBox) return;

      const scrollLeft = schedulerBox.scrollLeft;
      const viewportWidth = schedulerBox.clientWidth;
      const zoomFactor = zoom / 100;

      // Adjust for zoom factor
      const adjustedScroll = scrollLeft / zoomFactor;
      const adjustedViewport = viewportWidth / zoomFactor;
      const timelineWidth = 60; // $column-height from scss

      requestAnimationFrame(() => {
        if (timelineLeftRef.current) {
          timelineLeftRef.current.style.transform = `translateX(${adjustedScroll}px)`;
        }
        if (timelineRightRef.current) {
          // Position right timeline at right edge of visible viewport
          const rightPosition =
            adjustedScroll + adjustedViewport - timelineWidth;
          timelineRightRef.current.style.transform = `translateX(${rightPosition}px)`;
        }
      });
    };

    const handleScroll = () => updateTimelinePositions();
    const handleResize = () => updateTimelinePositions();

    const schedulerBox = schedulerBoxRef.current;
    if (schedulerBox) {
      schedulerBox.addEventListener("scroll", handleScroll, { passive: true });
      window.addEventListener("resize", handleResize);
      // Initial position
      updateTimelinePositions();
      return () => {
        schedulerBox.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [zoom]);

  useEffect(() => {
    const schedulerBox = schedulerBoxRef.current;
    if (schedulerBox) {
      schedulerBox.scrollLeft = 0;
    }
  }, [view]);

  useEffect(() => {
    if (!loadEmpty) {
      setSuppressEmptyCard(false);
    }
  }, [loadEmpty]);

  useEffect(() => {
    if (
      !suppressEmptyCard ||
      !loadEmpty ||
      !pendingCreateFromEmptyRef.current
    ) {
      return;
    }
    let cancelled = false;
    let innerTimeoutId = null;
    const openCreateDialog = () => {
      if (cancelled) return false;
      const cal = calendarRef.current?.scheduler;
      if (!cal) return false;
      const base = dayjs(selectedDate).tz(SCHEDULER_TIMEZONE).startOf("day");
      const start = base
        .hour(SCHEDULER_START_HOUR)
        .minute(0)
        .second(0)
        .millisecond(0)
        .toDate();
      const end = base
        .hour(SCHEDULER_START_HOUR)
        .minute(30)
        .second(0)
        .millisecond(0)
        .toDate();
      cal.triggerDialog(true, { start, end });
      pendingCreateFromEmptyRef.current = false;
      if (!dataset.stylists?.length) {
        notify(
          "Add at least one stylist to your salon before booking appointments.",
          "warning",
          5000
        );
      }
      return true;
    };
    const outerTimeoutId = window.setTimeout(() => {
      if (cancelled) return;
      if (openCreateDialog()) return;
      innerTimeoutId = window.setTimeout(() => {
        if (cancelled) return;
        if (!openCreateDialog()) {
          pendingCreateFromEmptyRef.current = false;
        }
      }, 400);
    }, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(outerTimeoutId);
      if (innerTimeoutId != null) window.clearTimeout(innerTimeoutId);
    };
  }, [suppressEmptyCard, loadEmpty, selectedDate, dataset.stylists?.length]);

  // JD-293: Re-fetch when location changes (no key-based remount).
  // fetchRemote already has location in its useCallback deps and has
  // race-condition protection via fetchIdRef.
  useEffect(() => {
    fetchRemote();
  }, [location?.location_id]);

  const adjustUI = () => {
    const items = document.querySelectorAll(".rs__event__item");

    items.forEach((item) => {
      const scheduleItem = item.querySelector(".schedule-item");
      if (scheduleItem) {
        const startTime = scheduleItem.getAttribute("data-start");
        const endTime = scheduleItem.getAttribute("data-end");

        const startHour =
          view === "week" ? weekConfig.startHour : dayConfig.startHour;
        const step = view === "week" ? weekConfig.step : dayConfig.step;

        const parseTime = (hhmm) => {
          if (!hhmm) return null;
          const [h, m] = hhmm.split(":").map(Number);
          if (Number.isNaN(h) || Number.isNaN(m)) return null;
          return { h, m };
        };

        const startParts = parseTime(startTime);
        const endParts = parseTime(endTime);
        if (!startParts || !endParts) return;

        const dayStart = dayjs(selectedDate)
          .startOf("day")
          .hour(startHour)
          .minute(0)
          .second(0);
        const startMoment = dayjs(selectedDate)
          .hour(startParts.h)
          .minute(startParts.m)
          .second(0);
        const endMoment = dayjs(selectedDate)
          .hour(endParts.h)
          .minute(endParts.m)
          .second(0);

        const minutesFromStart = startMoment.diff(dayStart, "minute");
        const top = Math.floor(minutesFromStart / step) * 30 + "px";

        const duration = endMoment.diff(startMoment, "minute");
        const slots =
          duration > 0 ? Math.max(1, Math.ceil(duration / step)) : 1;
        const height = slots * 30 + "px";

        item.style.top = top;
        item.style.height = height;
      }
    });
  };

  const syncAppointmentGroupHover = () => {
    const root =
      schedulerWrapperRef.current ||
      document.querySelector(".scheduler-wrapper");
    if (!root) return () => {};

    let activeGroupId = null;

    const groupEventEls = (groupId) => {
      if (!groupId) return [];
      return Array.from(root.querySelectorAll(".schedule-item"))
        .filter(
          (item) => item.getAttribute("data-appointment-id") === String(groupId)
        )
        .map((item) => item.closest(".rs__event__item"))
        .filter(Boolean);
    };

    const clearActive = () => {
      root
        .querySelectorAll(".rs__event__item.appointment-group-hover")
        .forEach((el) => el.classList.remove("appointment-group-hover"));
      activeGroupId = null;
    };

    const setActive = (nextGroupId) => {
      if (!nextGroupId) return;
      if (nextGroupId !== activeGroupId) {
        clearActive();
        activeGroupId = nextGroupId;
      }
      groupEventEls(nextGroupId).forEach((el) =>
        el.classList.add("appointment-group-hover")
      );
    };

    const onOver = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const scheduleItem = target.closest(".schedule-item");
      if (!scheduleItem) return;
      const groupId = scheduleItem.getAttribute("data-appointment-id") || "";
      if (groupId) setActive(groupId);
    };

    const onOut = (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const leavingFrom = target.closest(".schedule-item");
      if (!leavingFrom) return;
      const related = e.relatedTarget;
      if (related instanceof Element) {
        const entering = related.closest(".schedule-item");
        if (
          entering &&
          entering.getAttribute("data-appointment-id") ===
            leavingFrom.getAttribute("data-appointment-id")
        ) {
          return;
        }
      }
      clearActive();
    };

    root.addEventListener("mouseover", onOver, true);
    root.addEventListener("mouseout", onOut, true);
    return () => {
      root.removeEventListener("mouseover", onOver, true);
      root.removeEventListener("mouseout", onOut, true);
      clearActive();
    };
  };

  const markUnavailableCells = () => {
    if (!resources || resources.length === 0) return;

    const startHour =
      view === "week" ? weekConfig.startHour : dayConfig.startHour;
    const step = view === "week" ? weekConfig.step : dayConfig.step;

    if (view === "day") {
      // DAY VIEW: Multiple resources (stylists) in columns
      // Find all resource containers (columns)
      const resourceContainers = document.querySelectorAll(
        ".css-1mrufi > div, .css-12aktbw > div"
      );

      resourceContainers.forEach((container, containerIndex) => {
        // Map container index to resource
        // First container includes time column, so adjust index
        const resourceIndex = containerIndex === 0 ? 0 : containerIndex;
        const stylist = resources[resourceIndex] || resources[0];

        if (!stylist) return;

        // Find all time slot cells in this container
        const cells = container.querySelectorAll(
          ".css-1gyej37 .rs__cell:not(.rs__header):not(.rs__time)"
        );

        cells.forEach((cell, cellIndex) => {
          cell.classList.remove("unavailable-cell");

          const totalMinutes = startHour * 60 + cellIndex * step;
          const hours = Math.floor(totalMinutes / 60);
          const minutes = totalMinutes % 60;
          const cellTime = dayjs(selectedDate)
            .hour(hours)
            .minute(minutes)
            .second(0);

          if (isCellUnavailable(stylist, cellTime)) {
            cell.classList.add("unavailable-cell");
          }
        });
      });
    }
  };

  useEffect(() => {
    if (!loading && (view === "week" || view === "day")) {
      adjustUI();
      // Use a small delay to ensure DOM is fully rendered
      setTimeout(() => {
        markUnavailableCells();
      }, 100);
    }
  });

  useEffect(() => {
    if (loading) return;
    const cleanup = syncAppointmentGroupHover();

    return () => {
      cleanup?.();
    };
  }, [loading, view, zoom, events?.length]);

  const refreshData = async () => {
    setSyncing(true);
    setSyncMessage("Syncing...");

    try {
      // Get S3 URL from API
      setSyncMessage("Refreshing schedule...");
      const res = await baseAxios.post(SchedulerAPI.get, userCredentials);

      // Check if response contains dataUrl (new S3 pattern) or direct data (old pattern)
      let data;
      if (res.data.dataUrl) {
        // Fetch actual data from S3 without auth headers
        setSyncMessage("Loading appointments...");
        const s3Response = await axios.get(res.data.dataUrl);
        data = s3Response.data;
      } else {
        // Fallback to direct data (backwards compatibility)
        data = res.data;
      }

      setSyncMessage("Almost done...");
      const result = {
        ...data,
        stylists: data?.stylists?.map((stylist) => ({
          ...stylist,
          color: stringToColor(stylist.user_name),
        })),
        schedules: groupedSchedules(data?.schedules, SCHEDULER_TIMEZONE),
        locations: buildLocations(data?.locations),
      };

      if (Array.isArray(data?.stylistOrder)) {
        setStylistOrder(parseStylistOrder(data.stylistOrder));
      }

      // Only replace dataset here. The selectedDate/view effect applies
      // the visible-day filter to events/resources — setting all schedules
      // into events first remounts the calendar as empty for other days.
      setDataset(result);
    } catch (error) {
      console.error("Error refreshing scheduler data:", error);
      toast.error("Failed to sync schedule", { duration: 3000 });
    } finally {
      setSyncing(false);
      setSyncMessage("");
    }
  };

  const handleDelete = async (deletedId) => {
    const eventTodelete = dataset.schedules.find(
      (x) => x.appointmentId === deletedId
    );

    if (!eventTodelete) {
      notify("Scheduled event does not exist.", "Error", 3000);
      return;
    }

    const res = await baseAxios.delete(SchedulerAPI.delete, {
      data: {
        scheduleEntryId: deletedId,
      },
    });
    if (res.status === 200) {
      refreshData();

      return deletedId;
    }
  };

  /**
   * The stylist's time off that moving `appointment` to `nextStart` (and
   * `nextUserId`) would enter, or null. Mirrors the API rule: only when the
   * stylist and start stay the same do blocks the stored span already
   * overlapped not count; any real move into time off is refused.
   */
  const findMoveTimeOffConflict = (
    appointment,
    nextStart,
    nextUserId = appointment.user_id,
    original = appointment
  ) => {
    const stored = getAppointmentTimeRange(original, dataset.schedules);
    const start = dayjs(nextStart);
    return findTimeOffOverlap(
      dataset.timeOff,
      nextUserId,
      start,
      start.add(stored.durationMinutes, "minute"),
      { userId: original.user_id, start: stored.start, end: stored.end }
    );
  };

  const onMove = async (e, time, data, originalEvent) => {
    if (data?.isTimeOff) return;
    if (data?.appointment_status === ScheduleEntryStatus.Canceled) {
      toast.error("Cancelled appointments cannot be moved.", {
        duration: 3000,
      });
      return;
    }
    if (
      findMoveTimeOffConflict(data, time, data.user_id, originalEvent || data)
    ) {
      toast.error(STYLIST_TIME_OFF_ERROR);
      return;
    }
    const formatted = formatScheduleUpdateStart(time);

    try {
      const res = await baseAxios.put(SchedulerAPI.modify, {
        start: formatted,
        user_id: data.user_id,
        location_id: data.location.location_id,
        client_id: data.client.client_id,
        services: data.uniqueServices,
        schedule_entry_id: data.appointmentId,
      });

      if (res.status === 200) {
        refreshData();
        return {};
      }
    } catch (err) {
      if (isStylistTimeOffError(err)) {
        toast.error(err.response.data.message);
        return;
      }
      throw err;
    }
  };

  const jumpSchedulerToStart = (start) => {
    if (!start) return;
    const nextSelected = dayjs(start).tz(SCHEDULER_TIMEZONE).toDate();
    if (!dayjs(nextSelected).isSame(selectedDate, "day")) {
      setSelectedDate(nextSelected);
      calendarRef.current?.scheduler?.handleState(nextSelected, "selectedDate");
    }
  };

  const handleRescheduleAppointment = async (appointment, nextStart) => {
    if (!appointment?.appointmentId || !nextStart) return;

    try {
      const services = getAppointmentServicesForUpdate(appointment);
      if (!services.length) {
        throw new Error("Missing service list for this appointment.");
      }

      const { durationMinutes } = getAppointmentTimeRange(
        appointment,
        dataset.schedules
      );
      const nextStartDayjs = dayjs(nextStart);
      const nextEnd = nextStartDayjs.add(durationMinutes, "minute");
      const stylist = dataset.stylists?.find(
        (s) => s.user_id === appointment.user_id
      );

      const hoursCheck = validateSchedulerBookingHours(
        nextStartDayjs,
        nextEnd
      );
      if (!hoursCheck.valid) {
        throw new Error(hoursCheck.message || SCHEDULER_HOURS_ERROR);
      }

      const availabilityCheck = validateStylistAvailabilityForAppointment(
        stylist,
        nextStartDayjs,
        nextEnd
      );
      if (!availabilityCheck.valid) {
        throw new Error(
          availabilityCheck.message || STYLIST_AVAILABILITY_ERROR
        );
      }

      if (findMoveTimeOffConflict(appointment, nextStartDayjs)) {
        throw new Error(STYLIST_TIME_OFF_ERROR);
      }

      const formatted = formatScheduleUpdateStart(nextStart);

      const res = await baseAxios.put(SchedulerAPI.modify, {
        start: formatted,
        user_id: appointment.user_id,
        location_id: appointment.location?.location_id,
        client_id: appointment.client?.client_id,
        services,
        schedule_entry_id: appointment.appointmentId,
        booking_source: appointment.booking_source,
      });

      if (res.status !== 200) return;

      // Load fresh data before changing the visible day — otherwise the calendar
      // filters the new date against stale events and the appointment vanishes.
      await refreshData();
      jumpSchedulerToStart(nextStart);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Could not reschedule appointment";
      toast.error(msg);
      throw err;
    }
  };

  // JD-293: handleLocation removed — location is now managed by the global header selector.

  useEffect(() => {
    if (dataset.schedules && dataset.stylists) {
      // Filter schedules for the selected date and location
      const filteredSchedule = dataset.schedules.filter((schedule) => {
        let scheduleInLocation = true;
        if (location && location.location_id !== -1) {
          scheduleInLocation =
            schedule.location.location_id === location.location_id;
        }

        return (
          dayjs(schedule.start).isSame(selectedDate, view) && scheduleInLocation
        );
      });

      // Filter stylists based on location AND availability for the selected date
      const filteredStylists = dataset.stylists.filter((stylist) => {
        // First, check location filter
        let inLocation = true;
        if (location && location.location_id !== -1) {
          inLocation = worksAtLocation(stylist, location.location_id);
        }

        if (!inLocation) return false;

        // For week view, check if stylist has availability on ANY day of the week
        if (view === "week") {
          const weekStart = dayjs(selectedDate).startOf("week");
          // Check if stylist has availability on at least one day of the week
          for (let i = 0; i < 7; i++) {
            const dayToCheck = weekStart.add(i, "day");
            const dayAvailability = getStylistDayAvailability(
              stylist,
              dayToCheck
            );
            if (dayAvailability) {
              return true; // Stylist has availability on at least one day this week
            }
          }
          return false; // No availability on any day this week
        }

        // For day view, check if stylist has availability on the selected date
        const dayAvailability = getStylistDayAvailability(
          stylist,
          selectedDate
        );
        return dayAvailability !== null; // Only show if stylist has availability on this day
      });

      const stylistsWithAvailability = sortByStylistOrder(
        filteredStylists.map((stylist) => ({
          ...stylist,
          ...getStylistAvailabilityStats(
            stylist,
            dataset.schedules,
            selectedDate,
            dataset.timeOff
          ),
        })),
        stylistOrder
      );

      const visibleUserIds = new Set(
        stylistsWithAvailability.map((s) => s.user_id)
      );
      const visibleEvents = filteredSchedule.filter((e) =>
        visibleUserIds.has(e.user_id)
      );
      // Time off applies at every location: only the stylist and day matter.
      const visibleTimeOff = timeOffToEvents(dataset.timeOff).filter(
        (e) =>
          visibleUserIds.has(e.user_id) &&
          dayjs(e.start).isSame(selectedDate, view)
      );
      const calendarEvents = [
        ...visibleSchedulerEvents(visibleEvents),
        ...visibleTimeOff,
      ];

      calendarRef.current?.scheduler?.handleState(
        stylistsWithAvailability,
        "resources"
      );
      setResources(stylistsWithAvailability);
      calendarRef.current?.scheduler?.handleState(calendarEvents, "events");
      // `events` stays appointments only; time-off events are grid-only.
      setEvents(visibleEvents);
      setTimeOffEvents(visibleTimeOff);
    }
  }, [selectedDate, view, dataset, location, stylistOrder]);

  const handleConfirmDialog = async (payload) => {
    if (!payload.appointmentId || !payload.dialogType) return;

    let res;

    switch (payload.dialogType) {
      case ConfirmationType.CHECK_IN:
        res = await baseAxios.post("/scheduler/checkin", {
          appointmentId: payload.appointmentId,
          isLate: payload.isLate,
        });
        if (res.status === 200) {
          notify("Client checked in", "success", 3000);
          refreshData();
        }
        break;
      case ConfirmationType.CHECK_OUT:
        {
          const appointmentToCheckout = findSchedule(payload.appointmentId);
          if (appointmentToCheckout) {
            setCheckoutData(appointmentToCheckout);
          }
          setDialogPayload(null);
        }
        break;
      case ConfirmationType.CANCEL:
        setCancellingAppointmentId(payload.appointmentId);
        break;
      case ConfirmationType.NO_SHOW:
        res = await baseAxios.post("/scheduler/noShow", {
          appointmentId: payload.appointmentId,
        });
        if (res.status === 200) {
          notify("Appointment marked as No Show", "success", 3000);
          refreshData();
        }
        break;
      case ConfirmationType.LATE:
        res = await baseAxios.post("/scheduler/late", {
          appointmentId: payload.appointmentId,
        });
        if (res.status === 200) {
          notify("Client marked late", "success", 3000);
          refreshData();
        }
        break;
    }

    if (res && res?.status !== 200) {
      notify("Error processing request", "Error", 3000);
    }
    setDialogPayload(null);
  };

  const handleCheckoutConfirm = async (checkoutDetails) => {
    try {
      const res = await baseAxios.post("/scheduler/checkout", {
        appointmentId: checkoutData.appointmentId,
        paymentMethod: checkoutDetails.paymentMethod,
        receiptEmail: checkoutDetails.emailAddress,
        services: checkoutDetails.savedSelectedServices,
        clientId: checkoutData.client.client_id,
        stylistId: checkoutData.user_id,
        locationId: checkoutData.location.location_id,
        totalAmount: checkoutDetails.totalAmount,
        tip: checkoutDetails.tipAmount,
      });

      if (res.status === 200) {
        notify("Client checked out successfully", "success", 3000);
        refreshData();
      }
    } catch (error) {
      notify("Error during checkout", "error", 3000);
    }
    setCheckoutData(null);
  };

  const handleCloseDialog = () => {
    setDialogPayload(null);
  };

  const handleSaveColumnOrder = async (orderedStylists) => {
    const subsetOrder = (orderedStylists || [])
      .map((stylist) => Number(stylist.user_id))
      .filter((id) => Number.isInteger(id) && id > 0);
    const merged = mergeStylistOrder(stylistOrder, subsetOrder);

    setSavingColumnOrder(true);
    try {
      const res = await baseAxios.put(SchedulerAPI.columnOrder, {
        stylistOrder: merged,
      });
      const saved = parseStylistOrder(res.data?.stylistOrder);
      setStylistOrder(saved.length ? saved : merged);
      setRearrangeOpen(false);
      toast.success("Column order saved");
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Could not save column order";
      toast.error(message);
    } finally {
      setSavingColumnOrder(false);
    }
  };

  const findSchedule = (appointmentId) => {
    if (appointmentId == null || appointmentId === "") return null;
    const match = (s) => String(s?.appointmentId) === String(appointmentId);
    return (
      (events || []).find(match) ||
      (dataset?.schedules || []).find(match) ||
      null
    );
  };

  const onStartAppointment = (appointmentOrId) => {
    const fromEvent =
      appointmentOrId && typeof appointmentOrId === "object"
        ? appointmentOrId
        : null;
    const appointmentId = fromEvent
      ? fromEvent.appointmentId
      : appointmentOrId;
    const appointment =
      fromEvent?.appointmentId != null
        ? fromEvent
        : findSchedule(appointmentId);

    if (!appointment?.appointmentId) {
      notify("Appointment not found", "error", 3000);
      refreshData();
      return;
    }

    const currentTimeUtc = dayjs().tz(SCHEDULER_TIMEZONE);
    const appointmentStartUtc = dayjs(
      appointment.scheduleStart ?? appointment.start
    ).tz(SCHEDULER_TIMEZONE);
    const minutesDiff = currentTimeUtc.diff(appointmentStartUtc, "minute");
    const isLate = Boolean(
      appointment.is_late || appointment.isLate || minutesDiff > 10
    );

    setDialogPayload({
      isLate,
      appointmentId: appointment.appointmentId,
      dialogType: ConfirmationType.CHECK_IN,
    });
  };

  const onFinishAppointment = (appointmentId) => {
    setDialogPayload({ appointmentId, dialogType: ConfirmationType.CHECK_OUT });
  };

  const onCancelAppointment = (appointmentId) => {
    setDialogPayload({ appointmentId, dialogType: ConfirmationType.CANCEL });
  };

  const onNoShow = (appointmentId) => {
    setDialogPayload({ appointmentId, dialogType: ConfirmationType.NO_SHOW });
  };

  const onMarkAsLate = (appointmentId) => {
    setDialogPayload({ appointmentId, dialogType: ConfirmationType.LATE });
  };

  const handleCancelAppointmentConfirmed = async (reasonId) => {
    try {
      const res = await baseAxios.post("/scheduler/cancel", {
        appointmentId: cancellingAppointmentId,
        reasonId,
      });
      if (res.status === 200) {
        notify("Appointment canceled", "success", 3000);
        refreshData();
      }
    } catch (error) {
      notify(
        error?.data?.message || "Could not cancel appointment",
        "error",
        4000
      );
    } finally {
      setCancellingAppointmentId(null);
    }
  };

  const handleCopyAppointment = (appointmentId) => {
    setAppointmentIdToCopy(appointmentId);
  };
  const handleSnackbarClose = () => {
    setAppointmentIdToCopy(null);
    processingCopyRef.current = false;
  };

  const WALK_IN_REGISTER_CLOSED_MESSAGE =
    "Before recording a walk-in transaction, please open the register first.";

  // Walk-in Transaction Handlers
  const handleRegisterClick = async () => {
    if (!hasSpecificLocation) {
      toast.error(
        "Please select a specific location before creating a walk-in transaction."
      );
      return;
    }

    let registerOpen = isRegisterOpen;
    try {
      const res = await baseAxios.get("/register/status", {
        params: { locationId: location.location_id },
      });
      setRegisterStatus(res.data);
      registerOpen = Boolean(res.data?.isOpen);
    } catch (err) {
      console.error("Failed to load register status", err);
      registerOpen = false;
    }

    if (!registerOpen) {
      toast.error(WALK_IN_REGISTER_CLOSED_MESSAGE);
      return;
    }

    setTransactionFormData(null); // Reset form data
    setShowTransactionForm(true);
  };

  const handleTransactionFormConfirm = (data) => {
    if (!isRegisterOpen) {
      toast.error(WALK_IN_REGISTER_CLOSED_MESSAGE);
      return;
    }
    setTransactionData(data);
    setTransactionFormData({
      clientId: data.clientId,
      selectedClient: data.selectedClient,
      selectedLocation: data.selectedLocation,
      items: data.items,
    });
    setShowTransactionForm(false);
    setShowTransactionSummary(true);
  };

  const handleTransactionFormClose = () => {
    setShowTransactionForm(false);
    setTransactionFormData(null);
  };

  const handleTransactionSummaryBack = (data) => {
    // Preserve the data and go back to form
    setTransactionFormData({
      clientId: data.clientId,
      selectedClient: data.selectedClient,
      selectedLocation: data.selectedLocation,
      items: data.items,
    });
    setShowTransactionSummary(false);
    setShowTransactionForm(true);
  };

  const handleTransactionSummaryClose = () => {
    setShowTransactionSummary(false);
    setTransactionData(null);
    setTransactionFormData(null);
  };

  const handleTransactionSuccess = () => {
    // Clear all transaction state
    setTransactionData(null);
    setTransactionFormData(null);
    // Optionally refresh data if needed
    refreshData();
  };

  const handleProcessCopyAppointment = async (newDate, appointmentId) => {
    try {
      setLoading(true);

      if (processingCopyRef.current) {
        return;
      }
      processingCopyRef.current = true;

      const appointmentEvents = events.filter(
        (e) => e.appointmentId === appointmentId
      );
      if (!appointmentEvents.length) {
        notify("No services found for this appointment.", "error", 3000);
        processingCopyRef.current = false;
        setAppointmentIdToCopy(null);
        setLoading(false);
        return;
      }

      const originalStart = appointmentEvents[0].start;
      const newStart = new Date(newDate);
      const offsetMs = newStart - new Date(originalStart);

      // Time off is a hard stop (the API refuses it), not a confirm.
      const copiedSegments = appointmentEvents.flatMap(
        (event) => event.services || []
      );
      const copyStartMs = Math.min(
        ...copiedSegments.map((s) => dayjs(s.serviceStart).valueOf())
      );
      const copyEndMs = Math.max(
        ...copiedSegments.map((s) => dayjs(s.serviceEnd).valueOf())
      );
      if (
        copiedSegments.length > 0 &&
        findTimeOffOverlap(
          dataset.timeOff,
          appointmentEvents[0].user_id,
          copyStartMs + offsetMs,
          copyEndMs + offsetMs
        )
      ) {
        toast.error(STYLIST_TIME_OFF_ERROR);
        processingCopyRef.current = false;
        return;
      }

      let overlapStart = null;
      for (const event of appointmentEvents) {
        for (const service of event.services) {
          const origServiceStart = dayjs(service.serviceStart).tz(
            SCHEDULER_TIMEZONE
          );
          const origServiceEnd = dayjs(service.serviceEnd).tz(
            SCHEDULER_TIMEZONE
          );
          const newServiceStart = origServiceStart.add(offsetMs, "ms");
          const newServiceEnd = origServiceEnd.add(offsetMs, "ms");

          const todayEvents = events.filter(
            (x) =>
              x.user_id === event.user_id &&
              x.appointment_status !== ScheduleEntryStatus.Canceled &&
              new Date(
                dayjs(x.start).year(),
                dayjs(x.start).month(),
                dayjs(x.start).date()
              ).getTime() ===
                new Date(
                  newServiceStart.year(),
                  newServiceStart.month(),
                  newServiceStart.date()
                ).getTime()
          );
          for (const otherEvent of todayEvents) {
            for (const otherService of otherEvent.services) {
              const otherStart = new Date(otherService.serviceStart).getTime();
              const otherEnd = new Date(otherService.serviceEnd).getTime();
              if (
                !isBlockingTimeRange(newServiceStart, newServiceEnd) ||
                !isBlockingTimeRange(otherStart, otherEnd)
              ) {
                continue;
              }

              if (newServiceStart <= otherStart && newServiceEnd > otherStart) {
                if (
                  !overlapStart ||
                  otherStart < overlapStart.toDate().getTime()
                ) {
                  overlapStart = dayjs(otherStart).utc(false);
                }
              } else if (
                newServiceStart > otherStart &&
                newServiceStart < otherEnd
              ) {
                if (!overlapStart || newServiceStart < overlapStart) {
                  overlapStart = newServiceStart;
                }
              }
            }
          }
        }
      }

      if (overlapStart) {
        const confirmResult = window.confirm(
          `Upon scheduling this service, the stylist will be double-booked starting at ${dayjs(
            overlapStart
          )
            .utc(true)
            .format("h:mm A")}. Would you like to proceed?`
        );

        if (!confirmResult) {
          await new Promise((resolve) => setTimeout(resolve, 300));
          processingCopyRef.current = false;
          return;
        }
      }

      const res = await baseAxios.post("/scheduler/copyAppointment", {
        appointmentId: appointmentId,
        start: dayjs(newDate).format("YYYY-MM-DD HH:mm:ss"),
      });

      if (res.status === 200) {
        notify("Appointment copied", "success", 3000);
        setAppointmentIdToCopy(null);
        await refreshData();
        processingCopyRef.current = false;
      }
    } catch (error) {
      console.error("Error during copy appointment:", error);
      if (isStylistTimeOffError(error)) {
        toast.error(error.response.data.message);
      } else {
        notify("Error during copy appointment", "error", 3000);
      }
      processingCopyRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDeleteTimeOff = async () => {
    if (!timeOffToDelete) return;
    setDeletingTimeOff(true);
    try {
      await deleteTimeOff(timeOffToDelete.id);
      toast.success("Time off deleted");
      setTimeOffToDelete(null);
      refreshData();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not delete time off");
    } finally {
      setDeletingTimeOff(false);
    }
  };

  viewerApiRef.current = {
    canManageTimeOff,
    stylists: dataset?.stylists || [],
    openTimeOffEdit: (timeOff) => setTimeOffDialog({ timeOff }),
    requestTimeOffDelete: (timeOff) => setTimeOffToDelete(timeOff),
    appointmentIdToCopy,
    resources,
    events,
    handleDelete,
    handleCopyAppointment,
    handleRescheduleAppointment,
    onStartAppointment,
    onFinishAppointment,
    onMarkAsLate,
    onCancelAppointment,
    onNoShow,
    refreshData,
  };

  return (
    <div
      ref={schedulerWrapperRef}
      className={`scheduler-wrapper viewmode-${view}`}
      style={{ ["--scheduler-custom-header-height"]: "0px" }}
    >
      <div
        className="content-block"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h2 style={{ margin: "0px" }}>Service Scheduler</h2>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ViewMode fetchRemote={fetchRemote} />
          {canManageTimeOff && (
            <Button
              sx={registerButtonSx}
              variant="contained"
              color="secondary"
              startIcon={<EventBusyIcon />}
              onClick={() => setTimeOffDialog({})}
            >
              Time off
            </Button>
          )}
          {canManageRegister && (
            isRegisterOpen ? (
              <>
                <Button
                  sx={registerButtonSx}
                  variant="contained"
                  color="primary"
                  onClick={handleOpeningClick}
                  disabled={!registerReady}
                >
                  Opening
                </Button>
                <Button
                  sx={registerButtonSx}
                  variant="contained"
                  color="warning"
                  onClick={handleCloseoutClick}
                  disabled={!registerReady}
                >
                  Closeout
                </Button>
              </>
            ) : (
              <Button
                sx={{
                  ...registerButtonSx,
                  maxWidth: 360,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                variant="contained"
                color="primary"
                onClick={handleOpenRegisterClick}
                disabled={!registerReady}
              >
                {location?.location_name
                  ? `Open Register · ${location.location_name}`
                  : "Open Register"}
              </Button>
            )
          )}
        </Box>
      </div>
      <div className={"content-block"} style={{ position: "relative" }}>
        <div
          ref={schedulerBoxRef}
          className={`scheduler-box ${loading ? "loading" : ""}`}
        >
          {/* Loading state - fetching/downloading data */}
          {loading && (
            <div className="scheduler-status-card scheduler-status-loading">
              <CircularProgress
                size={40}
                thickness={4}
                className="scheduler-loading-spinner"
              />
              <p className="scheduler-status-message">
                {loadMessage || "Loading…"}
              </p>
              {loadProgress != null ? (
                <Box className="scheduler-progress-wrap">
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <span className="scheduler-status-progress">
                      {loadProgress.total > 0
                        ? Math.round(
                            (loadProgress.loaded / loadProgress.total) * 100
                          )
                        : 0}
                      %
                    </span>
                    <span className="scheduler-status-progress-meta">
                      {loadProgress.total > 0
                        ? `${formatBytes(loadProgress.loaded)} / ${formatBytes(
                            loadProgress.total
                          )}`
                        : formatBytes(loadProgress.loaded)}
                    </span>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      loadProgress.total > 0
                        ? Math.min(
                            100,
                            (loadProgress.loaded / loadProgress.total) * 100
                          )
                        : 0
                    }
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: "rgba(25, 118, 210, 0.15)",
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              ) : null}
            </div>
          )}
          {!loading && loadError && (
            <div className="scheduler-status-card scheduler-status-error">
              <p className="scheduler-status-title">
                We could not load your schedule
              </p>
              <p className="scheduler-status-message">{loadError}</p>
              <p className="scheduler-status-hint">
                If this keeps happening, check your internet connection or try
                again later.
              </p>
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={() => fetchRemote()}
                sx={{ mt: 1 }}
              >
                Try again
              </Button>
            </div>
          )}
          {!loading && !loadError && loadEmpty && !suppressEmptyCard && (
            <div className="scheduler-status-card scheduler-status-empty">
              <p className="scheduler-status-title">
                No appointments right now
              </p>
              <p className="scheduler-status-message">
                There are no appointments to show for the selected location.
                <br />
                Try choosing a different location above, or check back later
                when new appointments are added.
              </p>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 1,
                  justifyContent: "center",
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EventAvailableIcon />}
                  onClick={() => {
                    pendingCreateFromEmptyRef.current = true;
                    setSuppressEmptyCard(true);
                  }}
                >
                  Create appointment
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchRemote()}
                >
                  Refresh schedule
                </Button>
              </Box>
            </div>
          )}
          {!loading &&
            !loadError &&
            (!loadEmpty || suppressEmptyCard) &&
            (dataset.stylists?.length > 0 ||
              events?.length > 0 ||
              suppressEmptyCard) && (
              <ThemeProvider theme={schedulerMuiTheme}>
                <div
                  className="scheduler-zoom-container"
                  style={{
                    position: "relative",
                    zoom: `${zoom}%`,
                  }}
                >
                <SchedulerCustomHeader
                  ref={schedulerCustomHeaderRef}
                  view={view}
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  onViewChange={setView}
                  calendarRef={calendarRef}
                  jumpTo={
                    <WeekNavigation
                      onDateChange={setSelectedDate}
                      calendarRef={calendarRef}
                      onRegisterClick={handleRegisterClick}
                      onViewChange={setView}
                      timeZone={SCHEDULER_TIMEZONE}
                    />
                  }
                  weekStartOn={weekConfig.weekStartOn}
                  timeZone={SCHEDULER_TIMEZONE}
                  onRearrangeClick={() => setRearrangeOpen(true)}
                  onPrintClick={() => setPrintScheduleOpen(true)}
                />
                {currentQuarterTop !== null && (
                  <div
                    className="scheduler-current-quarter-overlay"
                    style={{ top: `${currentQuarterTop}px` }}
                  />
                )}
                <TimeLine
                  ref={timelineLeftRef}
                  position="left"
                  isTop={resources?.length === 0}
                />
                <TimeLine
                  ref={timelineRightRef}
                  position="right"
                  isTop={resources?.length === 0}
                />
                <Scheduler
                  key={`${appointmentIdToCopy || "scheduler"}-${view}`}
                  ref={calendarRef}
                  timeZone={SCHEDULER_TIMEZONE}
                  events={[
                    ...visibleSchedulerEvents(events),
                    ...timeOffEvents,
                  ]}
                  resources={resources}
                  agenda={false}
                  navigation={false}
                  disableViewNavigator
                  selectedDate={selectedDate}
                  onSelectedDateChange={setSelectedDate}
                  view={view}
                  week={weekConfig}
                  day={dayConfig}
                  onViewChange={(view) => setView(view)}
                  resourceFields={resourceFields}
                  resourceHeaderComponent={(resource) => (
                    <ResourceHeader resource={resource} />
                  )}
                  eventRenderer={(props) =>
                    renderCell(props, appointmentIdToCopy)
                  }
                  customViewer={(event, close) => {
                    const api = viewerApiRef.current;
                    const viewer = event.isTimeOff ? (
                      <TimeOffViewer
                        event={event}
                        stylists={api.stylists}
                        canManage={api.canManageTimeOff}
                        onEdit={() => {
                          close();
                          api.openTimeOffEdit(event.timeOff);
                        }}
                        onDelete={() => {
                          close();
                          api.requestTimeOffDelete(event.timeOff);
                        }}
                      />
                    ) : (
                      <AppointmentView
                        event={event}
                        close={close}
                        appointmentIdToCopy={api.appointmentIdToCopy}
                        resources={api.resources}
                        events={api.events}
                        calendarRef={calendarRef}
                        handleDelete={api.handleDelete}
                        handleCopyAppointment={api.handleCopyAppointment}
                        onReschedule={api.handleRescheduleAppointment}
                        onStartAppointment={api.onStartAppointment}
                        onFinishAppointment={api.onFinishAppointment}
                        onMarkAsLate={api.onMarkAsLate}
                        onCancelAppointment={api.onCancelAppointment}
                        onNoShow={api.onNoShow}
                        refreshData={api.refreshData}
                      />
                    );
                    return mode === "dark" ? (
                      <ThemeProvider theme={editorTheme}>{viewer}</ThemeProvider>
                    ) : (
                      viewer
                    );
                  }}
                  fields={getFields({ stylists: resources })}
                  customEditor={(scheduler) => {
                    if (appointmentIdToCopy) {
                      handleProcessCopyAppointment(
                        scheduler.state.start.value,
                        appointmentIdToCopy
                      );
                      scheduler.close();
                      return null;
                    }

                    const editor = (
                      <CustomEditor
                        refreshData={refreshData}
                        scheduler={scheduler}
                        dataset={dataset}
                        onAppointmentSaved={jumpSchedulerToStart}
                      />
                    );
                    return mode === "dark" ? (
                      <ThemeProvider theme={editorTheme}>{editor}</ThemeProvider>
                    ) : (
                      editor
                    );
                  }}
                  disableViewer={false}
                  onDelete={handleDelete}
                  onEventDrop={onMove}
                  draggable={true}
                />
                </div>
              </ThemeProvider>
            )}
        </div>
        <ZoomWidget zoom={zoom} onZoomChange={setZoom} />
      </div>
      {Boolean(dialogPayload) && (
        <ConfirmationModal
          payload={dialogPayload}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmDialog}
        />
      )}
      <TimeOffDialog
        open={Boolean(timeOffDialog)}
        timeOff={timeOffDialog?.timeOff}
        initialDate={selectedDate}
        stylists={dataset?.stylists || []}
        onClose={() => setTimeOffDialog(null)}
        onSaved={() => refreshData()}
      />
      <TimeOffDeleteDialog
        block={timeOffToDelete}
        loading={deletingTimeOff}
        onClose={() => setTimeOffToDelete(null)}
        onConfirm={handleConfirmDeleteTimeOff}
      />
      <CancelModal
        open={Boolean(cancellingAppointmentId)}
        onClose={() => setCancellingAppointmentId(null)}
        onConfirm={({ reasonId }) => {
          handleCancelAppointmentConfirmed(reasonId);
        }}
      />
      <PrintStylistScheduleDialog
        open={printScheduleOpen}
        onClose={() => setPrintScheduleOpen(false)}
        stylists={resources || []}
        events={dataset?.schedules || events || []}
        selectedDate={selectedDate}
        timeZone={SCHEDULER_TIMEZONE}
        locationName={
          location?.location_id !== -1 ? location?.location_name || "" : ""
        }
        salonAddress={
          location?.location_id !== -1
            ? (dataset?.schedules || []).find(
                (item) =>
                  item.location?.location_id === location.location_id &&
                  item.salon_address
              )?.salon_address || ""
            : ""
        }
      />
      <RearrangeColumnsModal
        open={rearrangeOpen}
        stylists={dataset?.stylists || []}
        stylistOrder={stylistOrder}
        saving={savingColumnOrder}
        onClose={() => {
          if (!savingColumnOrder) setRearrangeOpen(false);
        }}
        onSave={handleSaveColumnOrder}
      />
      {Boolean(checkoutData) && (
        <CheckoutSummaryModal
          open={Boolean(checkoutData)}
          onClose={() => {
            setCheckoutData(null);
          }}
          onConfirm={handleCheckoutConfirm}
          appointment={checkoutData}
        />
      )}
      <Snackbar
        open={!!appointmentIdToCopy}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="warning"
          sx={{ width: "100%" }}
        >
          Select a time and date to make a copy of the appointment.
        </Alert>
      </Snackbar>

      {/* Background sync indicator */}
      {syncing && (
        <div
          style={{
            position: "absolute",
            bottom: "150px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10000,
            backgroundColor: "background.paper",
            borderRadius: "20px",
            padding: "8px 16px",
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.2)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <CircularProgress size={16} thickness={4} />
          <span style={{ fontSize: "14px" }}>{syncMessage}</span>
        </div>
      )}

      {/* Walk-in Transaction Form Modal */}
      <TransactionFormModal
        open={showTransactionForm}
        onClose={handleTransactionFormClose}
        onConfirm={handleTransactionFormConfirm}
        dataset={dataset}
        initialData={transactionFormData}
        refreshData={refreshData}
      />

      {/* Walk-in Transaction Summary Modal */}
      <TransactionSummaryModal
        open={showTransactionSummary}
        onClose={handleTransactionSummaryClose}
        onBack={handleTransactionSummaryBack}
        transactionData={transactionData}
        onSuccess={handleTransactionSuccess}
      />

      {/* Register dialogs are rendered globally by `RegisterDialogManager` */}
    </div>
  );
}

export { SCHEDULER_TIMEZONE } from "./config";

export default CustomScheduler;
